import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PollyManageBackendService } from '..';
import { indexedDbKeys } from '../../datasets/manage-indexed-db-keys.dataset';
import { WorkspaceFile } from '../../interfaces';
import { IndexedDBPacket } from 'polly-commons';

/**
 * @description This class contains the content array and currently selected destination folder path as observables,
 * which we can subscribe to and get data whenever needed during file copy and move operations.
 *
 * @injectable in workspace files copy and move operational components
 */
@Injectable()
export class PollyManageFilesOperationsDataService {
  public workspaceContentArray$: Observable<any[]>;
  public currentFolderPath$: Observable<string>;
  public destinationFolderPath$: Observable<string>;
  public workspaceContentNextUrls$: Observable<string[]>;
  public selectedWorkspace$: Observable<number | null>;
  private readonly workspaceContentArray: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private readonly currentFolderPath: Subject<string> = new Subject<string>();
  private readonly destinationFolderPath: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly workspaceContentNextUrls: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private readonly selectedWorkspace: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  constructor(private readonly backendService: PollyManageBackendService) {
    this.currentFolderPath$ = this.currentFolderPath.asObservable();
    this.destinationFolderPath$ = this.destinationFolderPath.asObservable();
    this.workspaceContentArray$ = this.workspaceContentArray.asObservable();
    this.workspaceContentNextUrls$ = this.workspaceContentNextUrls.asObservable();
    this.selectedWorkspace$ = this.selectedWorkspace.asObservable();
  }

  /**
   * Sets Workspace Content Array for currently selected workspace
   *
   * @param data content array
   */
  public setWorkspaceContentArray(data: any[]): void {
    this.workspaceContentArray.next(data);
  }

  /**
   * @description Sets current folder path when we go into the folder in workspace
   *
   * @param path path to be set for current folder clicked
   */
  public setCurrentFolderPath(path: string): void {
    this.currentFolderPath.next(path);
  }

  /**
   * @description Sets destination folder path when we go into the folder in workspace
   * @use to update the content list when copy/move is initiated
   *
   * @param path path to be set for destination folder clicked
   */
  public setDestinationFolderPath(path: string): void {
    this.destinationFolderPath.next(path);
  }

  /**
   * @description Sets the workspace selected from dropdown as selected workspace
   *
   * @param workspaceId Id of the workspace
   */
  public setSelectedWorkspace(workspaceId: number): void {
    this.selectedWorkspace.next(workspaceId);
  }

  /**
   * @description Returns the selected destination workspace ID for files copy/move operation
   *
   * @return (number | null)
   */
  public getSelectedWorkspace(): number | null {
    return this.selectedWorkspace.getValue();
  }

  /**
   * @description Updates the workspace content list by updating indexedDB when copy or move is initiated.
   *
   * @param filesPath Files path in workspace
   * @param selectedWorkspaceFiles files needs to added as disabled items on content list
   * @param action Action being performed on the content item
   * @param path Whether we are updating source list or destination list
   */
  public updateWorkspaceContentList(
    filesPath: string,
    selectedWorkspaceFiles: WorkspaceFile[],
    action: 'copy' | 'move',
    path: 'source' | 'destination'
  ): Observable<IndexedDBPacket<WorkspaceFile[]>> {
    // store selected workspace files to be copied/moved in new DB with `source or destination` prefix
    const key: string = `${path}-${indexedDbKeys.workspace}-${filesPath}`;
    const workspaceFiles: WorkspaceFile[] = [];
    selectedWorkspaceFiles.forEach((selectedFile) => {
      workspaceFiles.push({ ...selectedFile, action });
    });
    return this.backendService.indexedDB.setDB<WorkspaceFile[]>(key, workspaceFiles);
  }
}
